import React, { useEffect } from "react";
import { EditOutlined } from "@ant-design/icons";
import { Form, Input, Select, Button, Switch } from "antd";
import TextArea from "antd/es/input/TextArea";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import { useListService } from "../../../hooks/api/service/hooks";
import { IServiceList, IServicePayload } from "../../../types/api/service";

const OurServicesSection: React.FC<{
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  form: any;
  ourServiceData?: {
    heading?: string;
    subtext?: string;
    selectedServices?: string[] | { _id: string; order: number }[]; // can
    isHidden?: boolean;
  };
  loading?: boolean;
}> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  loading,
  ourServiceData,
}) => {
  useEffect(() => {
  if (ourServiceData) {
    form.setFieldsValue({
      our_services: {
        heading: ourServiceData.heading || "",
        subtext: ourServiceData.subtext || "",

        // ✅ handles both string[] and object[]
        selectedServices:
          ourServiceData.selectedServices?.map((item) =>
            typeof item === "string" ? item : item._id
          ) || [],
      },
    });
  } else {
    form.resetFields();
  }
}, [ourServiceData, form]);

  const { data } = useListService({ page: 1, limit: 20, isAll: true });
  const serviceList: IServiceList[] = data?.data || [];
  const selectedServices =
    Form.useWatch(["our_services", "selectedServices"], form) || [];

  return (
    <div>
      {!isEditing ? (
        <>
          {/* Desktop View */}
          <div className="rounded-[20px] overflow-hidden">
            <table className="overflow-x-auto hidden min-w-full sm:table">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Subtext</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {ourServiceData ? (
                  <tr className="bg-[#F9F9F9] border-b border-[#e9e8e8]">
                    <td className="py-3 px-4">{ourServiceData.heading}</td>
                    <td className="py-3 px-4">{ourServiceData.subtext}</td>
                    <td className="py-3 px-4  text-primary">
                      <div className="flex items-center gap-4 justify-end">
                        <EditOutlined
                          onClick={() => setIsEditing(true)}
                          className="cursor-pointer hover:text-blue-600 text-[20px]"
                        />
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={3} className="text-center py-6 text-gray-500">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add data
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="bg-[#F9F9F9] border-b border-[#e9e8e8] sm:block hidden">
              <div className="py-3 px-4">
                <Form layout="vertical" form={form}>
                  <Form.Item
                    label="Select Services (Max 3)"
                    name={["our_services", "selectedServices"]}
                    rules={[
                      {
                        validator: (_, value) =>
                          value && value.length > 3
                            ? Promise.reject(
                                new Error("You can select maximum 3 services"),
                              )
                            : Promise.resolve(),
                      },
                    ]}
                  >
                    <Select
                      mode="multiple"
                      placeholder="Select up to 3 services"
                      className="w-full"
                      maxTagCount={3}
                      showSearch
                      optionFilterProp="label"
                      options={serviceList.map((service) => ({
                        label: service.heading,
                        value: service._id, // same id as selectedServices
                        disabled:
                          selectedServices.length >= 3 &&
                          !selectedServices.includes(service._id),
                      }))}
                    />
                  </Form.Item>
                </Form>

                <ButtonWrapper
                  type="primary"
                  onClick={handleSave}
                  loading={loading}
                >
                  Save
                </ButtonWrapper>
              </div>
            </div>
          </div>

          <table className="min-w-full table sm:hidden">
            <tbody>
              {ourServiceData ? (
                <tr className="bg-[#F9F9F9] border block mb-4 rounded-lg shadow-sm">
                  <td className="py-3 px-4 block">
                    <span className="font-semibold text-primary block mb-1">
                      Heading:
                    </span>
                    {ourServiceData.heading}
                  </td>
                  <td className="py-3 px-4 block">
                    <span className="font-semibold text-primary block mb-1">
                      Subtext:
                    </span>
                    {ourServiceData.subtext}
                  </td>

                  <td className="py-3 px-4 text-primary block text-left">
                    <div>
                      <span className="font-semibold text-primary block my-1">
                        Action:
                      </span>
                      <EditOutlined
                        onClick={() => setIsEditing(true)}
                        className="cursor-pointer text-black text-[18px]"
                      />
                    </div>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td className="text-center py-6">
                    <Button type="dashed" onClick={() => setIsEditing(true)}>
                      Add data
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="bg-[#F9F9F9] border-b border-[#e9e8e8] rounded-lg sm:hidden block">
            <div className="py-3 px-4">
              <Form layout="vertical" form={form}>
                <Form.Item
                  label="Select Services (Max 3)"
                  name={["our_services", "selectedServices"]}
                  rules={[
                    {
                      validator: (_, value) =>
                        value && value.length > 3
                          ? Promise.reject(
                              new Error("Maximum 3 services allowed"),
                            )
                          : Promise.resolve(),
                    },
                  ]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select up to 4 services"
                    className="w-full"
                    maxTagCount={3}
                    showSearch
                    value={selectedServices}
                    optionFilterProp="label"
                    options={serviceList.map((service) => ({
                      label: service.heading,
                      value: service._id,
                      disabled:
                        selectedServices.length >= 4 &&
                        !selectedServices.includes(service._id),
                    }))}
                  />
                </Form.Item>
              </Form>

              <ButtonWrapper
                type="primary"
                onClick={handleSave}
                loading={loading}
              >
                Save
              </ButtonWrapper>
            </div>
          </div>
        </>
      ) : (
        /* EDIT MODE FORM */
        <div>
          <div className="bg-[#F9F9F9] p-4 rounded-lg shadow-sm">
            <Form layout="vertical" form={form}>
              <Form.Item
                label="Heading"
                name={["our_services", "heading"]}
                rules={[{ required: true, message: "Please enter heading" }]}
              >
                <Input placeholder="Heading" />
              </Form.Item>

              <Form.Item
                label="Subtext"
                name={["our_services", "subtext"]}
                rules={[{ required: true, message: "Please enter subtext" }]}
              >
                <TextArea placeholder="Subtext" rows={3} />
              </Form.Item>
            </Form>
          </div>

          <div className="flex gap-3 mt-6">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper onClick={handleCancel}>Cancel</ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default OurServicesSection;
